import React, { useState, useEffect, useRef } from 'react';
import { phoneAuthService, PhoneAuthResult, PhoneVerificationResult } from '../services/phoneAuth';
import { firebaseApiService } from '../services/firebaseApi';
import { useAuth } from '../contexts/AuthContext';
import { User } from 'firebase/auth';
import './PhoneVerifyModal.css';

interface PhoneVerifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerificationSuccess: () => void;
}

const PhoneVerifyModal: React.FC<PhoneVerifyModalProps> = ({
  isOpen,
  onClose,
  onVerificationSuccess
}) => {
  const { user } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'phone' | 'verification'>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const recaptchaRef = useRef<HTMLDivElement>(null);

  // Timer for resend code
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Setup recaptcha when component mounts (hidden)
  useEffect(() => {
    if (isOpen && recaptchaRef.current) {
      try {
        phoneAuthService.initializeRecaptcha('recaptcha-container');
      } catch (err: any) {
        console.error('Error setting up recaptcha:', err);
        setError('Không thể khởi tạo xác thực. Vui lòng thử lại.');
      }
    }
  }, [isOpen]);

  // Handle code input changes
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear error when user starts typing
    if (error) setError('');
    
    // Only allow digits and limit to 6 characters
    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
    setVerificationCode(value);
  };

  // Get full verification code
  const getFullVerificationCode = () => {
    return verificationCode.replace(/\s/g, '');
  };

  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // If starts with 0, replace with +84
    if (digits.startsWith('0')) {
      return `+84${digits.substring(1)}`;
    }
    
    // If starts with 84, add +
    if (digits.startsWith('84')) {
      return `+${digits}`;
    }
    
    // If doesn't start with country code, assume Vietnam
    return `+84${digits}`;
  };

  const handleSendCode = async () => {
    if (!phoneNumber.trim()) {
      setError('Vui lòng nhập số điện thoại');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      console.log('Sending verification code to:', formattedPhone);
      
      const result: PhoneAuthResult = await phoneAuthService.sendVerificationCode(formattedPhone);
      
      if (result.success) {
        setStep('verification');
        setTimer(60); // 60 seconds timer
        console.log('Verification code sent successfully');
      } else {
        setError(result.error || 'Có lỗi xảy ra khi gửi mã xác thực');
      }
    } catch (error: any) {
      console.error('Error sending verification code:', error);
      setError(error.message || 'Có lỗi xảy ra khi gửi mã xác thực');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const fullCode = getFullVerificationCode();
    
    console.log('Full code:', fullCode, 'Length:', fullCode.length);
    
    if (!fullCode || fullCode.length !== 6) {
      setError('Vui lòng nhập đủ 6 số của mã xác thực');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Verifying code:', fullCode);
      
      const result: PhoneVerificationResult = await phoneAuthService.verifyCode(fullCode);
      
      if (result.success && result.user) {
        console.log('Phone verification successful, linking with backend...');
        
        // Get the idToken from the verified user
        const idToken = await result.user.getIdToken();
        console.log('Got idToken for backend verification');
        
        // Send to phone verify API với userId và idToken để map user
        if (user?.id) {
          const backendResult = await firebaseApiService.verifyPhoneAuth(user.id);
          
          if (backendResult.success) {
            console.log('Phone verification and backend linking successful');
            
            // Show success message briefly before closing
            setError(''); // Clear any errors
            
            onVerificationSuccess();
            onClose();
            // Reset form
            setPhoneNumber('');
            setVerificationCode('');
            setStep('phone');
          } else {
            console.error('Backend linking failed:', backendResult.error);
            setError(backendResult.error || 'Có lỗi xảy ra khi liên kết với hệ thống');
          }
        } else {
          setError('Không tìm thấy thông tin người dùng');
        }
      } else {
        setError(result.error || 'Mã xác thực không đúng. Vui lòng thử lại.');
      }
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setError(error.message || 'Có lỗi xảy ra khi xác thực. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (timer > 0) return;
    
    setTimer(60);
    setError('');
    
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      await phoneAuthService.sendVerificationCode(formattedPhone);
      console.log('Verification code resent successfully');
    } catch (error: any) {
      console.error('Error resending verification code:', error);
      setError(error.message || 'Có lỗi xảy ra khi gửi lại mã');
    }
  };

  const handleClose = () => {
    setPhoneNumber('');
    setVerificationCode('');
    setStep('phone');
    setError('');
    setTimer(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="phone-verify-modal-overlay" onClick={handleClose}>
      <div className="phone-verify-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="phone-verify-modal-header">
          <h2>Cập nhật số điện thoại</h2>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="phone-verify-modal-body">
          {step === 'phone' ? (
            <div className="phone-input-step">
              <div className="step-info">
                <h3>Nhập số điện thoại</h3>
                <p>Chúng tôi sẽ gửi mã xác thực đến số điện thoại của bạn</p>
              </div>

              <div className="input-group">
                <label htmlFor="phone">Số điện thoại</label>
                <input
                  id="phone"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Nhập số điện thoại (VD: 0987654321)"
                  disabled={loading}
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              {/* Hidden recaptcha */}
              <div id="recaptcha-container" ref={recaptchaRef} style={{ display: 'none' }}></div>

              <button
                className="primary-button"
                onClick={handleSendCode}
                disabled={loading || !phoneNumber.trim()}
              >
                {loading ? 'Đang gửi...' : 'Gửi mã xác thực'}
              </button>
            </div>
          ) : (
            <div className="verification-step">
              <div className="step-info">
                <h3>Nhập mã xác thực</h3>
                <p>
                  Mã xác thực đã được gửi đến số {formatPhoneNumber(phoneNumber)}
                </p>
              </div>

              <div className="input-group">
                <label htmlFor="code">Mã xác thực</label>
                <input
                  id="code"
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={verificationCode}
                  onChange={handleCodeChange}
                  placeholder="Nhập 6 chữ số"
                  maxLength={6}
                  disabled={loading}
                  className="code-input"
                  autoComplete="off"
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="verification-actions">
                <button
                  className="primary-button"
                  onClick={handleVerifyCode}
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? 'Đang xác thực...' : 'Xác thực'}
                </button>

                <div className="resend-section">
                  {timer > 0 ? (
                    <p className="timer-text">
                      Gửi lại mã sau {timer} giây
                    </p>
                  ) : (
                    <button
                      className="link-button"
                      onClick={handleResendCode}
                    >
                      Gửi lại mã xác thực
                    </button>
                  )}
                </div>
              </div>

              <button
                className="link-button"
                onClick={() => {
                  setStep('phone');
                  setVerificationCode('');
                  setError('');
                }}
              >
                ← Thay đổi số điện thoại
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneVerifyModal;
